import React, { useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { Calendar, Check } from "lucide-react";
import { Attachment } from "protogen/common_pb";
import { EmailMessage } from "protogen/conversation_pb";
import { CreateEventFromEmailAttachmentRequest } from "protogen/email_service_pb";
import { useCreateEventFromEmailAttachment } from "services/email";

interface IcsAttachmentButtonProps {
  email: EmailMessage;
  attachment: Attachment;
  attachmentIndex: number;
}

const IcsAttachmentButton: React.FC<IcsAttachmentButtonProps> = ({
  email,
  attachment,
  attachmentIndex,
}) => {
  const [processing, setProcessing] = useState(false);
  const [updated, setUpdated] = useState(false);
  const { request: createEventFromEmailAttachment } =
    useCreateEventFromEmailAttachment();

  const isIcsFile = (attachment: Attachment): boolean => {
    const contentType = attachment.contentType?.toLowerCase() || "";
    const filename = attachment.filename?.toLowerCase() || "";
    return (
      contentType.includes("text/calendar") ||
      contentType.includes("application/ics") ||
      contentType.includes("application/calendar") ||
      contentType.includes("text/x-vcalendar") ||
      filename.endsWith(".ics") ||
      filename.endsWith(".ical")
    );
  };

  const handleProcessEvent = async () => {
    if (!isIcsFile(attachment)) {
      return;
    }

    setProcessing(true);
    const request = new CreateEventFromEmailAttachmentRequest({
      emailRef: email.ref,
      attachmentIndex: attachmentIndex,
    });

    await createEventFromEmailAttachment(request);
    setProcessing(false);
    setUpdated(true);
  };

  if (!isIcsFile(attachment)) {
    return null;
  }

  const getIcon = () => {
    if (processing) return <CircularProgress size={16} />;
    if (updated) return <Check size={16} />;
    return <Calendar size={16} />;
  };

  const getText = () => {
    if (processing) return "Updating...";
    if (updated) return "Updated";
    return "Update Calendar";
  };

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={getIcon()}
      onClick={handleProcessEvent}
      disabled={processing || updated}
    >
      {getText()}
    </Button>
  );
};

export default IcsAttachmentButton;
